import React from 'react';
import type {
  CategorySearchFilters,
  CategorySearchResults
} from '../../types/rpc-contracts';
import type { VettingStatus } from '../../types/vetting';
import { useHashRoute } from '../../hooks/useHashRoute';
import { useCategoriesSearch } from '../../hooks/useCategoriesData';

const DEFAULT_LIMIT = 50;
const ALLOWED_LIMITS = new Set([25, 50, 100]);
const ALLOWED_SORTS = new Set<NonNullable<CategorySearchFilters['sort_by']>>([
  'program_name',
  'category_name',
  'display_name',
  'mapping_count',
  'tier',
  'bucket_level_1',
  'bucket_level_2',
  'bisac_codes'
]);
const ALLOWED_DIRECTIONS = new Set<NonNullable<CategorySearchFilters['sort_direction']>>(['asc', 'desc']);
const ALLOWED_MODES = new Set<NonNullable<CategorySearchFilters['search_mode']>>([
  'keyword',
  'semantic',
  'hybrid'
]);
const VETTING_STATUS_ORDER: VettingStatus[] = ['green', 'yellow', 'red'];
const DEFAULT_VETTING_FILTER: VettingStatus[] = ['green', 'yellow'];

type SearchMode = NonNullable<CategorySearchFilters['search_mode']>;

const sanitizeVettingSelection = (values?: Array<string | VettingStatus>): VettingStatus[] => {
  const normalizedSet = new Set<VettingStatus>();
  if (values && values.length > 0) {
    values.forEach((raw) => {
      const value = String(raw ?? '').trim().toLowerCase();
      switch (value) {
        case 'green':
        case 'yellow':
        case 'red':
          normalizedSet.add(value);
          break;
        case 'not suited':
        case 'not_suited':
        case 'not suitable':
        case 'not_suitable':
          normalizedSet.add('red');
          break;
        default:
          break;
      }
    });
  }

  if (normalizedSet.size === 0) {
    DEFAULT_VETTING_FILTER.forEach((status) => normalizedSet.add(status));
  }

  return VETTING_STATUS_ORDER.filter((status) => normalizedSet.has(status));
};

const isEqualStringArray = (a: string[], b: string[]): boolean => {
  if (a === b) {
    return true;
  }
  if (a.length !== b.length) {
    return false;
  }
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) {
      return false;
    }
  }
  return true;
};

export interface CategoriesQualityController {
  searchInput: string;
  setSearchInput: (value: string) => void;
  committedSearch: string;
  applySearch: () => void;
  clearSearch: () => void;
  searchMode: SearchMode;
  setSearchMode: (value: SearchMode) => void;
  hasBisac: boolean | undefined;
  setHasBisac: (value: boolean | undefined) => void;
  hasDisplayName: boolean | undefined;
  setHasDisplayName: (value: boolean | undefined) => void;
  hasBucketLevel1: boolean | undefined;
  setHasBucketLevel1: (value: boolean | undefined) => void;
  bucketLevel2: string[];
  setBucketLevel2: (values: string[]) => void;
  vettingStatuses: VettingStatus[];
  setVettingStatuses: (values: VettingStatus[]) => void;
  sortBy: NonNullable<CategorySearchFilters['sort_by']>;
  sortDirection: NonNullable<CategorySearchFilters['sort_direction']>;
  requestSort: (key: NonNullable<CategorySearchFilters['sort_by']>) => void;
  setSort: (
    key: NonNullable<CategorySearchFilters['sort_by']>,
    direction?: NonNullable<CategorySearchFilters['sort_direction']>
  ) => void;
  limit: number;
  setLimit: (value: number) => void;
  page: number;
  setPage: (value: number) => void;
  selectedIds: Set<string>;
  setSelectedIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  activeFilterCount: number;
  searchFilters: CategorySearchFilters;
  query: {
    data: CategorySearchResults | undefined;
    isLoading: boolean;
    isError: boolean;
    error: unknown;
    refetch: () => Promise<CategorySearchResults | undefined>;
  };
}

export function useCategoriesQualityController(): CategoriesQualityController {
  const { view, params, setParams } = useHashRoute('categories-quality');

  const [searchInput, setSearchInput] = React.useState('');
  const [committedSearch, setCommittedSearch] = React.useState('');
  const [searchMode, setSearchModeState] = React.useState<SearchMode>('keyword');
  const [hasBisac, setHasBisacState] = React.useState<boolean | undefined>(undefined);
  const [hasDisplayName, setHasDisplayNameState] = React.useState<boolean | undefined>(undefined);
  const [hasBucketLevel1, setHasBucketLevel1State] = React.useState<boolean | undefined>(undefined);
  const [bucketLevel2, setBucketLevel2State] = React.useState<string[]>([]);
  const [vettingStatuses, setVettingStatusesState] = React.useState<VettingStatus[]>(() =>
    sanitizeVettingSelection(DEFAULT_VETTING_FILTER)
  );
  const [sortBy, setSortBy] = React.useState<NonNullable<CategorySearchFilters['sort_by']>>('program_name');
  const [sortDirection, setSortDirection] = React.useState<NonNullable<CategorySearchFilters['sort_direction']>>('asc');
  const [limit, setLimitState] = React.useState<number>(DEFAULT_LIMIT);
  const [page, setPageState] = React.useState<number>(0);
  const [selectedIds, setSelectedIds] = React.useState<Set<string>>(new Set());

  // Use ref to track previous params to avoid circular dependency
  const prevParamsRef = React.useRef<Record<string, any>>({});

  // Sync from URL params into local state
  React.useEffect(() => {
    if (view !== 'categories-quality') {
      return;
    }

    const qParam = params.q ?? '';
    setSearchInput((current) => (current === qParam ? current : qParam));
    setCommittedSearch((current) => (current === qParam ? current : qParam));

    const bisacParam = params.bisac;
    const nextBisac = bisacParam === 'true' ? true : bisacParam === 'false' ? false : undefined;
    setHasBisacState((current) => (current === nextBisac ? current : nextBisac));

    const displayParam = params.display;
    const nextDisplay = displayParam === 'missing' ? false : displayParam === 'has' ? true : undefined;
    setHasDisplayNameState((current) => (current === nextDisplay ? current : nextDisplay));

    const bucketParam = params.bucket;
    const nextBucket = bucketParam === 'missing' ? false : bucketParam === 'has' ? true : undefined;
    setHasBucketLevel1State((current) => (current === nextBucket ? current : nextBucket));

    const bucket2Param = params.bucket2 ?? '';
    const nextBucket2 = bucket2Param ? bucket2Param.split(',').filter(Boolean) : [];
    setBucketLevel2State((current) => isEqualStringArray(current, nextBucket2) ? current : nextBucket2);

    const vetParam = params.vet ?? '';
    const vetTokens = vetParam ? vetParam.split(',').map((token) => token.trim()) : undefined;
    const nextVetting = sanitizeVettingSelection(vetTokens);
    setVettingStatusesState((current) => (isEqualStringArray(current, nextVetting) ? current : nextVetting));

    const sortByParam = params.sort_by as CategorySearchFilters['sort_by'] | null;
    const nextSortBy = sortByParam && ALLOWED_SORTS.has(sortByParam) ? sortByParam : 'program_name';
    setSortBy((current) => (current === nextSortBy ? current : nextSortBy));

    const sortDirParam = params.sort_dir as CategorySearchFilters['sort_direction'] | null;
    const nextSortDir = sortDirParam && ALLOWED_DIRECTIONS.has(sortDirParam) ? sortDirParam : 'asc';
    setSortDirection((current) => (current === nextSortDir ? current : nextSortDir));

    const limitParamRaw = params.limit ? parseInt(params.limit, 10) : undefined;
    const nextLimit = limitParamRaw && ALLOWED_LIMITS.has(limitParamRaw) ? limitParamRaw : DEFAULT_LIMIT;
    setLimitState((current) => (current === nextLimit ? current : nextLimit));

    const offsetParamRaw = params.offset ? parseInt(params.offset, 10) : 0;
    const normalizedOffset = Number.isFinite(offsetParamRaw) && offsetParamRaw > 0 ? offsetParamRaw : 0;
    const nextPage = nextLimit > 0 ? Math.floor(normalizedOffset / nextLimit) : 0;
    setPageState((current) => (current === nextPage ? current : nextPage));

    const smParam = params.sm as CategorySearchFilters['search_mode'] | null;
    const nextMode = smParam && ALLOWED_MODES.has(smParam) ? smParam : 'keyword';
    setSearchModeState((current) => (current === nextMode ? current : nextMode));
  }, [params, view]);

  const searchFilters = React.useMemo<CategorySearchFilters>(() => ({
    text_search: committedSearch.trim() || undefined,
    has_bisac: hasBisac,
    has_display_name: hasDisplayName,
    has_bucket_level_1: hasBucketLevel1,
    bucket_level_2: bucketLevel2.length ? [...bucketLevel2] : undefined,
    vetting_statuses: vettingStatuses.length === VETTING_STATUS_ORDER.length ? undefined : [...vettingStatuses],
    sort_by: sortBy,
    sort_direction: sortDirection,
    limit,
    offset: page > 0 ? page * limit : 0,
    search_mode: searchMode === 'keyword' ? undefined : searchMode,
  }), [committedSearch, hasBisac, hasDisplayName, hasBucketLevel1, bucketLevel2, vettingStatuses, sortBy, sortDirection, limit, page, searchMode]);

  // Sync state -> URL params when the view is active
  React.useEffect(() => {
    if (view !== 'categories-quality') {
      return;
    }

    const nextParams: Record<string, string | number | boolean | null | undefined> = {
      q: committedSearch.trim() || undefined,
      bisac: hasBisac === undefined ? undefined : String(hasBisac),
      display: hasDisplayName === undefined ? undefined : hasDisplayName ? 'has' : 'missing',
      bucket: hasBucketLevel1 === undefined ? undefined : hasBucketLevel1 ? 'has' : 'missing',
      bucket2: bucketLevel2.length ? bucketLevel2.join(',') : undefined,
      vet: vettingStatuses.length === VETTING_STATUS_ORDER.length ? undefined : vettingStatuses.join(','),
      sort_by: sortBy,
      sort_dir: sortDirection,
      limit: limit !== DEFAULT_LIMIT ? limit : undefined,
      offset: page > 0 ? page * limit : undefined,
      sm: searchMode !== 'keyword' ? searchMode : undefined,
    };

    // Only update URL if params actually changed
    const shallowEqual = (a: Record<string, any>, b: Record<string, any>) => {
      const aKeys = Object.keys(a).filter(k => a[k] !== undefined);
      const bKeys = Object.keys(b).filter(k => b[k] !== undefined);
      if (aKeys.length !== bKeys.length) return false;
      for (const k of aKeys) {
        if (a[k] !== b[k]) return false;
      }
      return true;
    };

    if (!shallowEqual(prevParamsRef.current, nextParams)) {
      prevParamsRef.current = nextParams;
      setParams(nextParams, { replaceAll: true });
    }
  }, [committedSearch, hasBisac, hasDisplayName, hasBucketLevel1, bucketLevel2, vettingStatuses, sortBy, sortDirection, limit, page, searchMode, setParams, view]);

  // Update ref when params change from external sources (browser back/forward)
  React.useEffect(() => {
    if (view === 'categories-quality') {
      prevParamsRef.current = params as any;
    }
  }, [params, view]);

  const queryResult = useCategoriesSearch(searchFilters, {
    placeholderData: (previous) => previous,
  });

  const applySearch = React.useCallback(() => {
    setCommittedSearch(searchInput.trim());
    setPageState(0);
    setSelectedIds(new Set());
  }, [searchInput]);

  const clearSearch = React.useCallback(() => {
    setSearchInput('');
    setCommittedSearch('');
    setPageState(0);
    setSelectedIds(new Set());
  }, []);

  const updateHasBisac = React.useCallback((value: boolean | undefined) => {
    setHasBisacState(value);
    setPageState(0);
    setSelectedIds(new Set());
  }, []);

  const updateHasDisplayName = React.useCallback((value: boolean | undefined) => {
    setHasDisplayNameState(value);
    setPageState(0);
    setSelectedIds(new Set());
  }, []);

  const updateHasBucketLevel1 = React.useCallback((value: boolean | undefined) => {
    setHasBucketLevel1State(value);
    setPageState(0);
    setSelectedIds(new Set());
  }, []);

  const updateBucketLevel2 = React.useCallback((values: string[]) => {
    setBucketLevel2State(values);
    setPageState(0);
    setSelectedIds(new Set());
  }, []);

  const updateVettingStatuses = React.useCallback((values: VettingStatus[]) => {
    const sanitized = sanitizeVettingSelection(values);
    setVettingStatusesState((current) => (isEqualStringArray(current, sanitized) ? current : sanitized));
    setPageState(0);
    setSelectedIds(new Set());
  }, []);

  const updateLimit = React.useCallback((value: number) => {
    const safeValue = ALLOWED_LIMITS.has(value) ? value : DEFAULT_LIMIT;
    setLimitState(safeValue);
    setPageState(0);
    setSelectedIds(new Set());
  }, []);

  const requestSort = React.useCallback((key: NonNullable<CategorySearchFilters['sort_by']>) => {
    // Calculate new values FIRST using current state from closure
    const isActive = sortBy === key;
    const newDirection = isActive
      ? (sortDirection === 'asc' ? 'desc' : 'asc')
      : 'asc';

    // Then update both states (React 18 batches these automatically)
    setSortBy(key);
    setSortDirection(newDirection);
    setPageState(0);
    setSelectedIds(new Set());
  }, [sortBy, sortDirection]);

  const setSort = React.useCallback((key: NonNullable<CategorySearchFilters['sort_by']>, direction: NonNullable<CategorySearchFilters['sort_direction']> = 'asc') => {
    const safeKey = ALLOWED_SORTS.has(key) ? key : 'program_name';
    const safeDirection = ALLOWED_DIRECTIONS.has(direction) ? direction : 'asc';
    setSortBy(safeKey);
    setSortDirection(safeDirection);
    setPageState(0);
    setSelectedIds(new Set());
  }, []);

  const setPage = React.useCallback((value: number) => {
    const nextPage = Math.max(0, value);
    setPageState(nextPage);
    setSelectedIds(new Set());
  }, []);

  const setSearchModeWithReset = React.useCallback((value: SearchMode) => {
    const nextMode = ALLOWED_MODES.has(value) ? value : 'keyword';
    setSearchModeState(nextMode);
    setPageState(0);
    setSelectedIds(new Set());
  }, []);

  const activeFilterCount =
    (hasBisac !== undefined ? 1 : 0) +
    (hasDisplayName !== undefined ? 1 : 0) +
    (hasBucketLevel1 !== undefined ? 1 : 0) +
    (bucketLevel2.length > 0 ? 1 : 0) +
    (vettingStatuses.length === VETTING_STATUS_ORDER.length ? 0 : 1);

  return {
    searchInput,
    setSearchInput,
    committedSearch,
    applySearch,
    clearSearch,
    searchMode,
    setSearchMode: setSearchModeWithReset,
    hasBisac,
    setHasBisac: updateHasBisac,
    hasDisplayName,
    setHasDisplayName: updateHasDisplayName,
    hasBucketLevel1,
    setHasBucketLevel1: updateHasBucketLevel1,
    bucketLevel2,
    setBucketLevel2: updateBucketLevel2,
    vettingStatuses,
    setVettingStatuses: updateVettingStatuses,
    sortBy,
    sortDirection,
    requestSort,
    setSort,
    limit,
    setLimit: updateLimit,
    page,
    setPage,
    selectedIds,
    setSelectedIds,
    activeFilterCount,
    searchFilters,
    query: {
      data: queryResult.data,
      isLoading: queryResult.isLoading,
      isError: queryResult.isError,
      error: queryResult.error,
      refetch: async () => {
        const result = await queryResult.refetch();
        return result.data;
      }
    }
  };
}
